import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Params, useParams } from 'react-router-dom';

import { Button, CurrenciesUSD, Icon, Input, Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { BankAccountCard, Loading, PageHeader, RenderFields, SectionHeader } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { TransferDirectionChip, TransferStatusChip, formatTransferType, getTransferIcon } from '~/lib/transfers';
import { getTransferURL } from '~/lib/transfers/utils';
import { PlatformModel } from '~/models/PlatformModel';
import { TransferListResponse } from '~/repositories';
import { TransferBatchRepository } from '~/repositories/Transfer/TransferBatchRepository';
import { TransferRepository } from '~/repositories/Transfer/TransferRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { TransferBatch, UnifiedTransfer, UpdateTransferBatchRequest } from '~/typings/API';

import { TransferBatchHeader } from './_components/TransferBatchHeader';

const columns: TableColumn<UnifiedTransfer>[] = [
  {
    header: 'Amount / Description',
    accessor: 'amount',
    width: { min: 150, max: '4fr' },
    cell: ({ row, value }) => (
      <Table.AmountCell
        currencyList={CurrenciesUSD}
        fixedCurrencyCode={row.currency}
        value={value}
        note={row.description ?? undefined}
        isBold
        isColorized
      />
    ),
  },
  {
    header: 'Type',
    accessor: 'type',
    cell: ({ value }) => (
      <Table.ChipCell value={formatTransferType(value)} variant="subtle" icon={getTransferIcon(value)} />
    ),
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 150, max: '1fr' },
    cell: ({ value }) => (
      <Table.Cell>
        <TransferStatusChip status={value} />
      </Table.Cell>
    ),
  },
  {
    header: 'Direction',
    accessor: 'isIncoming',
    width: { min: 120, max: '1fr' },
    cell: ({ row, value }) => (
      <Table.Cell>
        <TransferDirectionChip transferType={row.type} isIncoming={value} />
      </Table.Cell>
    ),
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { fixed: 80 },
    cell: Table.TimeCell,
  },
];

const transferBatchQueryKey = (platform: PlatformModel | undefined, transferBatchId: string | undefined) => [
  'transfers',
  'batches',
  platform?.id,
  transferBatchId,
];

export const PageTransferBatchesView: React.FC = () => {
  const { transferBatchId } = useParams<keyof Params>() as Params;
  const { currentPlatform } = useSessionStore();
  const navigate = useNavigate();
  const { handleFormErrors } = useFormErrorHandler();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { cursor, onNext, setPageSize, ...pagination } = usePaginationState();

  const queryClient = useQueryClient();
  const { data: transferBatch, isLoading: isTransferBatchLoading } = useQuery<TransferBatch>({
    queryKey: transferBatchQueryKey(currentPlatform, transferBatchId),
    queryFn: async () =>
      TransferBatchRepository.get({
        id: transferBatchId ?? '',
      }),
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
  });

  const { mutate: updateTransferBatch, isPending: isUpdateTransferBatchPending } = useMutation<
    TransferBatch,
    Error,
    UpdateTransferBatchRequest
  >({
    mutationFn: async (request) => TransferBatchRepository.update(transferBatchId ?? '', request),
    onSuccess: (data) => {
      queryClient.setQueryData(transferBatchQueryKey(currentPlatform, transferBatchId), data);
      addSuccessNotification({
        content: 'Transfer batch updated',
      });
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    },
  });

  const { data: transfersData, isLoading: isTransfersLoading } = useQuery<TransferListResponse>({
    queryKey: ['transfers', 'batches', currentPlatform, cursor, pagination.pageSize],
    queryFn: async () =>
      TransferRepository.getAll({
        limit: pagination.pageSize,
        startingAfter: cursor,
        transferBatchId: transferBatchId ?? '',
      }),
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
  });

  const formMethods = useForm<UpdateTransferBatchRequest>({
    defaultValues: {
      description: transferBatch?.description ?? '',
    },
  });
  useEffect(() => {
    formMethods.setValue('description', transferBatch?.description ?? '');
  }, [transferBatch?.description, transferBatch?.id]);

  return (
    <>
      <PageHeader text="Transfer Batch" />
      <Loading isLoading={isTransferBatchLoading} fullHeight>
        {transferBatch && (
          <TransferBatchHeader
            amount={transferBatch.totalAmount}
            createdAt={transferBatch.createdAt}
            currencyCode={transferBatch.totalCurrency}
            transferBatchId={transferBatch.id}
            transferCount={transferBatch.transferCount}
          />
        )}
        <SectionHeader text="Batch Details" border>
          <Button
            size="small"
            isDisabled={isUpdateTransferBatchPending}
            isLoading={isUpdateTransferBatchPending}
            onClick={formMethods.handleSubmit(
              (data) => {
                updateTransferBatch(data);
              },
              (errors) => handleFormErrors({ errors })
            )}
          >
            Save
          </Button>
        </SectionHeader>
        <Inner pt={0} px="var(--page-margin)">
          <form>
            <FormProvider {...formMethods}>
              <RenderFields
                sections={[
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'description',
                        label: 'Description',
                        children: ({ onBlur, onChange, value }, { isTouched, error }) => (
                          <Input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={String(value)}
                            hasError={isTouched && !!error}
                          />
                        ),
                      },
                      {
                        id: 'bankAccount',
                        label: 'Bank Account',
                        children: () =>
                          transferBatch?.bankAccountId ? (
                            <BankAccountCard bankAccountId={transferBatch.bankAccountId} />
                          ) : null,
                      },
                    ],
                  },
                ]}
              />
            </FormProvider>
          </form>
        </Inner>
        <SectionHeader text="Transfers" border />
        <Inner pt={0} px="var(--page-margin)">
          <Table<UnifiedTransfer>
            columns={columns}
            data={transfersData?.transfers ?? []}
            pagination={{
              hasNext: transfersData?.hasMore ?? false,
              onNext: () =>
                transfersData
                  ? onNext({ cursor: transfersData?.transfers[transfersData?.transfers.length - 1].id })
                  : undefined,
              onPageSizeChange: setPageSize,
              ...pagination,
            }}
            isLoading={isTransfersLoading}
            rowAction={(row) => (
              <Button
                onClick={(event: React.SyntheticEvent) => {
                  event.stopPropagation();
                  navigate(getTransferURL(row.id, row.type));
                }}
                icon={<Icon.ChevronRightLarge />}
                iconRight
                size="tiny"
                variant="secondary"
              >
                View
              </Button>
            )}
            variant={TableVariant.List}
            onRowClick={(row) => {
              navigate(getTransferURL(row.id, row.type));
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
